import { useEffect, useMemo, useState } from "react";
import {
  FiArrowLeft,
  FiSave,
  FiTruck,
  FiUser,
  FiFileText,
  FiCalendar,
  FiShoppingBag,
  FiLoader,
  FiAlertCircle,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

const emptyForm = {
  salesOrderId: "",
  deliveryDate: new Date().toISOString().slice(0, 10),
  transporter: "",
  vehicleNumber: "",
  notes: "",
};

export default function CreateDeliveryChallan() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [form, setForm] = useState(emptyForm);
  const [salesOrders, setSalesOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(true);
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const loadSalesOrders = async () => {
    setIsLoadingOrders(true);
    setError("");

    try {
      const response = await apiRequest("/sales-orders", { accessToken });

      const eligibleOrders = (Array.isArray(response) ? response : []).filter(
        (order) =>
          order.status === "CONFIRMED" ||
          order.status === "PARTIALLY_DELIVERED",
      );

      setSalesOrders(eligibleOrders);
    } catch (requestError) {
      setError(requestError.message || "Could not load sales orders.");
    } finally {
      setIsLoadingOrders(false);
    }
  };

  useEffect(() => {
    loadSalesOrders();
  }, [accessToken]);

  const selectSalesOrder = async (salesOrderId) => {
    updateField("salesOrderId", salesOrderId);
    setSelectedOrder(null);
    setItems([]);
    setError("");

    if (!salesOrderId) return;

    setIsLoadingOrder(true);

    try {
      const order = await apiRequest(`/sales-orders/${salesOrderId}`, {
        accessToken,
      });

      const dispatchableItems = (order.items || [])
        .map((item) => {
          const ordered = Number(item.quantityOrdered || 0);
          const delivered = Number(item.quantityDelivered || 0);
          const cancelled = Number(item.quantityCancelled || 0);
          const remaining = Math.max(ordered - delivered - cancelled, 0);

          return {
            salesOrderItemId: item.id,
            productName: item.productNameSnapshot,
            sku: item.skuSnapshot,
            variantName: item.variantNameSnapshot || "Default",
            ordered,
            dispatched: delivered,
            cancelled,
            remaining,
            quantityDispatched: remaining,
          };
        })
        .filter((item) => item.remaining > 0);

      setSelectedOrder(order);
      setItems(dispatchableItems);

      if (!dispatchableItems.length) {
        setError("This sales order has no remaining quantity to dispatch.");
      }
    } catch (requestError) {
      setError(requestError.message || "Could not load the selected sales order.");
    } finally {
      setIsLoadingOrder(false);
    }
  };

  const updateItemQuantity = (salesOrderItemId, value) => {
    setItems((currentItems) =>
      currentItems.map((item) => {
        if (item.salesOrderItemId !== salesOrderItemId) return item;

        const quantity = Number(value);
        const safeQuantity = Number.isFinite(quantity)
          ? Math.max(0, Math.min(quantity, item.remaining))
          : 0;

        return {
          ...item,
          quantityDispatched: safeQuantity,
        };
      }),
    );
  };

  const dispatchItems = useMemo(
    () =>
      items.filter(
        (item) => Number(item.quantityDispatched) > 0,
      ),
    [items],
  );

  const summary = useMemo(() => {
    const orderedQuantity = items.reduce(
      (total, item) => total + item.ordered,
      0,
    );

    const dispatchQuantity = dispatchItems.reduce(
      (total, item) => total + Number(item.quantityDispatched || 0),
      0,
    );

    const remainingQuantity = items.reduce(
      (total, item) =>
        total + Math.max(item.remaining - Number(item.quantityDispatched || 0), 0),
      0,
    );

    return {
      orderedQuantity,
      dispatchQuantity,
      remainingQuantity,
    };
  }, [items, dispatchItems]);

  const createChallan = async () => {
    setError("");

    if (!form.salesOrderId) {
      setError("Please select a sales order.");
      return;
    }

    if (!dispatchItems.length) {
      setError("Enter a dispatch quantity for at least one product.");
      return;
    }

    setIsSaving(true);

    try {
      const challan = await apiRequest("/delivery-challans", {
        method: "POST",
        accessToken,
        body: {
          salesOrderId: form.salesOrderId,
          deliveryDate: form.deliveryDate || undefined,
          transporter: form.transporter.trim() || undefined,
          vehicleNumber: form.vehicleNumber.trim() || undefined,
          notes: form.notes.trim() || undefined,
          items: dispatchItems.map((item) => ({
            salesOrderItemId: item.salesOrderItemId,
            quantityDispatched: Number(item.quantityDispatched),
          })),
        },
      });

      navigate(`/admindashboard/delivery/challan/${challan.id}`);
    } catch (requestError) {
      setError(requestError.message || "Could not create the delivery challan.");
    } finally {
      setIsSaving(false);
    }
  };

  const customer = selectedOrder?.customer;
  const store = selectedOrder?.store;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <button
            onClick={() => navigate("/admindashboard/delivery/challan/list")}
            className="flex items-center gap-2 text-gray-500 hover:text-primary-700 mb-3"
          >
            <FiArrowLeft />
            Back to Delivery Challans
          </button>

          <h1 className="text-4xl font-bold text-gray-900">
            Create Delivery Challan
          </h1>

          <p className="text-gray-500 mt-2">
            Create a draft against a confirmed sales order. Stock is issued only
            when the draft is dispatched.
          </p>
        </div>

        <button
          type="button"
          onClick={createChallan}
          disabled={isSaving || isLoadingOrder}
          className="h-12 px-6 rounded-xl bg-primary-700 text-white flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {isSaving ? <FiLoader className="animate-spin" /> : <FiSave />}
          Create Challan
        </button>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-red-700 flex gap-3">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          <p>{error}</p>
        </div>
      )}

      <div className="grid lg:grid-cols-4 gap-5">
        <StatusCard
          title="Status"
          value="Draft"
          icon={<FiFileText size={22} />}
          color="bg-blue-50 text-blue-700"
        />
        <StatusCard
          title="Warehouse"
          value={store?.name || "Select sales order"}
          icon={<FiTruck size={22} />}
          color="bg-green-50 text-green-700"
        />
        <StatusCard
          title="Delivery Date"
          value={
            form.deliveryDate
              ? new Date(`${form.deliveryDate}T00:00:00`).toLocaleDateString(
                  "en-IN",
                  { day: "2-digit", month: "short", year: "numeric" },
                )
              : "—"
          }
          icon={<FiCalendar size={22} />}
          color="bg-orange-50 text-orange-700"
        />
        <StatusCard
          title="Sales Order"
          value={selectedOrder?.salesOrderNumber || "Not selected"}
          icon={<FiShoppingBag size={22} />}
          color="bg-purple-50 text-purple-700"
        />
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 p-8">
        <h2 className="text-2xl font-bold mb-8">Delivery Challan Information</h2>

        <div className="grid lg:grid-cols-3 gap-6">
          <Input
            label="Delivery Challan No."
            value="Generated automatically"
            disabled
          />

          <Select
            label="Sales Order *"
            value={form.salesOrderId}
            onChange={selectSalesOrder}
            disabled={isLoadingOrders}
            options={[
              {
                value: "",
                label: isLoadingOrders
                  ? "Loading sales orders..."
                  : "Select confirmed sales order",
              },
              ...salesOrders.map((order) => ({
                value: order.id,
                label: `${order.salesOrderNumber} — ${order.customer?.fullName || "Walk-in customer"}`,
              })),
            ]}
          />

          <Input
            type="date"
            label="Delivery Date"
            value={form.deliveryDate}
            onChange={(value) => updateField("deliveryDate", value)}
          />

          <Input
            label="Transporter"
            value={form.transporter}
            placeholder="Optional"
            onChange={(value) => updateField("transporter", value)}
          />

          <Input
            label="Vehicle Number"
            value={form.vehicleNumber}
            placeholder="Optional"
            onChange={(value) => updateField("vehicleNumber", value)}
          />

          <Input
            label="Fulfillment Store"
            value={store ? `${store.name} (${store.code})` : "Auto-filled from order"}
            disabled
          />
        </div>
      </div>

      <div className="grid xl:grid-cols-2 gap-6">
        <InfoPanel
          title="Customer Information"
          subtitle="Auto-filled from the sales order"
          icon={<FiUser size={24} />}
          color="bg-primary-50 text-primary-700"
          rows={[
            ["Customer", customer?.fullName],
            ["Phone", customer?.phone],
            ["Email", customer?.email],
            ["Store", store?.name],
          ]}
        />

        <InfoPanel
          title="Sales Order Details"
          subtitle="Linked order information"
          icon={<FiFileText size={24} />}
          color="bg-green-50 text-green-700"
          rows={[
            ["Order Number", selectedOrder?.salesOrderNumber],
            [
              "Order Date",
              selectedOrder?.orderDate
                ? new Date(selectedOrder.orderDate).toLocaleDateString("en-IN")
                : null,
            ],
            [
              "Sales Executive",
              selectedOrder?.createdBy?.employee?.fullName ||
                selectedOrder?.createdBy?.email,
            ],
            ["Order Status", selectedOrder?.status?.replaceAll("_", " ")],
          ]}
        />
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center px-8 py-6 border-b">
          <div>
            <h2 className="text-2xl font-bold">Products To Dispatch</h2>
            <p className="text-gray-500 mt-2">
              Set the quantity to dispatch now. Stock availability is validated
              again when this challan is dispatched.
            </p>
          </div>

          <span className="px-4 py-2 rounded-full bg-primary-50 text-primary-700 font-semibold">
            {dispatchItems.length} selected
          </span>
        </div>

        {isLoadingOrder ? (
          <div className="py-16 flex justify-center text-primary-700">
            <FiLoader className="animate-spin mr-2" size={22} />
            Loading sales-order items...
          </div>
        ) : !selectedOrder ? (
          <EmptyState text="Select a confirmed sales order to load its pending items." />
        ) : !items.length ? (
          <EmptyState text="No remaining item quantity is available for this order." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[920px]">
              <thead className="bg-gray-50">
                <tr>
                  <HeaderCell>Product</HeaderCell>
                  <HeaderCell center>Variant</HeaderCell>
                  <HeaderCell center>Ordered</HeaderCell>
                  <HeaderCell center>Previously Delivered</HeaderCell>
                  <HeaderCell center>Remaining</HeaderCell>
                  <HeaderCell center>Dispatch Qty</HeaderCell>
                </tr>
              </thead>

              <tbody>
                {items.map((item) => (
                  <tr
                    key={item.salesOrderItemId}
                    className="border-b last:border-b-0 hover:bg-gray-50"
                  >
                    <td className="px-5 py-5">
                      <div className="flex items-center gap-4">
                        <div className="h-14 w-14 rounded-xl bg-gray-100 flex items-center justify-center">
                          <FiShoppingBag size={24} className="text-gray-400" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {item.productName}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1">
                            {item.sku}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="px-5 py-5 text-center">{item.variantName}</td>
                    <td className="px-5 py-5 text-center font-semibold">
                      {item.ordered}
                    </td>
                    <td className="px-5 py-5 text-center">
                      <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">
                        {item.dispatched}
                      </span>
                    </td>
                    <td className="px-5 py-5 text-center">
                      <span className="font-bold text-orange-600">
                        {item.remaining}
                      </span>
                    </td>
                    <td className="px-5 py-5 text-center">
                      <input
                        type="number"
                        min="0"
                        max={item.remaining}
                        step="0.001"
                        value={item.quantityDispatched}
                        onChange={(event) =>
                          updateItemQuantity(
                            item.salesOrderItemId,
                            event.target.value,
                          )
                        }
                        className="w-28 h-11 border border-gray-200 rounded-xl text-center focus:outline-none focus:border-primary-700"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid xl:grid-cols-4 gap-6">
        <MiniCard title="Products" value={items.length} />
        <MiniCard title="Ordered Qty" value={summary.orderedQuantity} />
        <MiniCard title="Dispatch Qty" value={summary.dispatchQuantity} />
        <MiniCard title="Remaining Qty" value={summary.remainingQuantity} />
      </div>

      <div className="grid xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 bg-white rounded-3xl border border-gray-100 p-8">
          <h2 className="text-2xl font-bold mb-6">Internal Notes</h2>
          <textarea
            rows={6}
            value={form.notes}
            onChange={(event) => updateField("notes", event.target.value)}
            placeholder="Packing, handling, or delivery notes..."
            className="w-full rounded-2xl border border-gray-200 p-4 resize-none focus:outline-none focus:border-primary-700"
          />
        </div>

        <div className="bg-white rounded-3xl border border-gray-100 p-6">
          <h2 className="text-2xl font-bold">Challan Summary</h2>

          <div className="space-y-5 mt-8">
            <Summary label="Products" value={items.length} />
            <Summary label="Dispatch Qty" value={summary.dispatchQuantity} />
            <Summary label="Warehouse" value={store?.name || "—"} />
            <Summary label="Transporter" value={form.transporter || "—"} />
            <Summary label="Vehicle" value={form.vehicleNumber || "—"} />
          </div>

          <div className="border-t my-6" />

          <p className="text-sm text-gray-500">
            Creating this challan saves a draft. Open its details page and use
            <strong className="text-gray-700"> Dispatch</strong> to issue stock
            and write inventory movements.
          </p>

          <button
            type="button"
            onClick={createChallan}
            disabled={isSaving || !selectedOrder || !dispatchItems.length}
            className="w-full mt-6 h-12 rounded-xl bg-primary-700 text-white font-medium disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {isSaving ? <FiLoader className="animate-spin" /> : <FiSave />}
            Create Delivery Challan
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusCard({ title, value, icon, color }) {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-5">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-gray-500">{title}</p>
          <h3 className="text-xl font-bold mt-2 truncate">{value}</h3>
        </div>
        <div className={`h-12 w-12 shrink-0 rounded-2xl flex items-center justify-center ${color}`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  type = "text",
  placeholder = "",
  disabled = false,
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-600 mb-2">
        {label}
      </label>
      <input
        type={type}
        value={value || ""}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(event) => onChange?.(event.target.value)}
        className="w-full h-12 rounded-xl border border-gray-200 px-4 focus:outline-none focus:border-primary-700 disabled:bg-gray-50 disabled:text-gray-500"
      />
    </div>
  );
}

function Select({ label, value, onChange, options, disabled = false }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-600 mb-2">
        {label}
      </label>
      <select
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className="w-full h-12 rounded-xl border border-gray-200 px-4 bg-white focus:outline-none focus:border-primary-700 disabled:bg-gray-50"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function InfoPanel({ title, subtitle, icon, color, rows }) {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-8">
      <div className="flex items-center gap-4 mb-6">
        <div className={`h-14 w-14 rounded-2xl flex items-center justify-center ${color}`}>
          {icon}
        </div>
        <div>
          <h2 className="text-2xl font-bold">{title}</h2>
          <p className="text-gray-500">{subtitle}</p>
        </div>
      </div>

      {rows.map(([label, value]) => (
        <div
          key={label}
          className="flex justify-between gap-5 py-4 border-b border-gray-100 last:border-b-0"
        >
          <span className="text-gray-500">{label}</span>
          <span className="font-semibold text-right">
            {value || "—"}
          </span>
        </div>
      ))}
    </div>
  );
}

function HeaderCell({ children, center = false }) {
  return (
    <th
      className={`px-5 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500 ${
        center ? "text-center" : "text-left"
      }`}
    >
      {children}
    </th>
  );
}

function EmptyState({ text }) {
  return <div className="py-16 text-center text-gray-500">{text}</div>;
}

function Summary({ label, value }) {
  return (
    <div className="flex justify-between items-center gap-4">
      <span className="text-gray-500">{label}</span>
      <span className="font-semibold text-right">{value}</span>
    </div>
  );
}

function MiniCard({ title, value }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5">
      <p className="text-sm text-gray-500">{title}</p>
      <h3 className="text-2xl font-bold mt-2">{value}</h3>
    </div>
  );
}